// ─── Parameter library types ─────────────────────────────────────────────────
export const PARAMETER_LIBRARY_TYPES = [
  'angle',
  'avatar',
  'problem_mechanism',
  'solution_mechanism',
  'ingredient',
  'awareness_level',
  'landing_page_url',
] as const;
export type ParameterLibraryType = (typeof PARAMETER_LIBRARY_TYPES)[number];

// ─── User roles ──────────────────────────────────────────────────────────────
export const ROLES = ['writer', 'admin', 'system_owner'] as const;
export type Role = (typeof ROLES)[number];

// ─── Run statuses ─────────────────────────────────────────────────────────────
export const RUN_STATUSES = [
  'draft',
  'running',
  'completed',
  'edited',
  'approved',
  'logged',
  'failed',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

export const RUN_MODES = [
  'use_existing_structure',
  'scratch_premise_set',
  'scratch_ad_draft',
  'fb_ad_to_advertorial',
  'competitor_advertorial_to_advertorial',
] as const;
export type RunMode = (typeof RUN_MODES)[number];

// Generation model catalog
export const GENERATION_MODEL_KEYS = ['codex', 'opus_4_8_extra', 'opus_4_6_max'] as const;
export type GenerationModelKey = (typeof GENERATION_MODEL_KEYS)[number];

export const DEFAULT_GENERATION_MODEL_KEY: GenerationModelKey = 'codex';

export const GENERATION_MODEL_PROVIDERS = ['openai', 'anthropic'] as const;
export type GenerationModelProvider = (typeof GENERATION_MODEL_PROVIDERS)[number];

export const GENERATION_MODEL_EFFORTS = ['xhigh', 'max'] as const;
export type GenerationModelEffort = (typeof GENERATION_MODEL_EFFORTS)[number];

export interface GenerationModelCatalogItem {
  key: GenerationModelKey;
  label: string;
  provider: GenerationModelProvider;
  providerLabel: string;
  providerModelId: string | null;
  effort: GenerationModelEffort;
  effortLabel: string;
}

export const GENERATION_MODEL_CATALOG = {
  codex: {
    key: 'codex',
    label: 'Codex',
    provider: 'openai',
    providerLabel: 'OpenAI-compatible',
    providerModelId: 'gpt-5.5',
    effort: 'xhigh',
    effortLabel: 'Extra',
  },
  opus_4_8_extra: {
    key: 'opus_4_8_extra',
    label: 'Claude Opus 4.8 Extra',
    provider: 'anthropic',
    providerLabel: 'Anthropic',
    providerModelId: 'claude-opus-4-8',
    effort: 'xhigh',
    effortLabel: 'Extra',
  },
  opus_4_6_max: {
    key: 'opus_4_6_max',
    label: 'Claude Opus 4.6 Max',
    provider: 'anthropic',
    providerLabel: 'Anthropic',
    providerModelId: 'claude-opus-4-6',
    effort: 'max',
    effortLabel: 'Max',
  },
} as const satisfies Record<GenerationModelKey, GenerationModelCatalogItem>;

// ─── Job statuses ─────────────────────────────────────────────────────────────
export const JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed', 'aborted'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

// Generation queue statuses
export const GENERATION_QUEUE_ITEM_STATUSES = [
  'queued',
  'running',
  'retrying',
  'completed',
  'failed',
  'cancelled',
] as const;
export type GenerationQueueItemStatus = (typeof GENERATION_QUEUE_ITEM_STATUSES)[number];

export const GENERATION_QUEUE_STATE_STATUSES = ['idle', 'running', 'paused'] as const;
export type GenerationQueueStateStatus = (typeof GENERATION_QUEUE_STATE_STATUSES)[number];

export const GENERATION_QUEUE_FAILURE_CATEGORIES = [
  'validation_error',
  'product_not_found',
  'invalid_market',
  'service_misconfigured',
  'model_api_error',
  'generation_timeout',
  'landing_page_fetch_failed',
  'persistence_error',
  'aborted_by_restart',
  'output_visibility_leak',
  'unexpected_server_error',
] as const;
export type GenerationQueueFailureCategory = (typeof GENERATION_QUEUE_FAILURE_CATEGORIES)[number];

// ─── Progress phase IDs (verbatim from MVP — K-table preserved) ──────────────
export const PROGRESS_PHASE_IDS = [
  'loading_sources',
  'building_prompt',
  'starting_model',
  'api_connected',
  'waiting_for_text',
  'first_text',
  'streaming',
  'saving',
  'complete',
] as const;
export type ProgressPhaseId = (typeof PROGRESS_PHASE_IDS)[number];

// ─── Cost constants (USD per million tokens) ──────────────────────────────────
export const INPUT_USD_PER_M = 5;
export const CACHED_INPUT_USD_PER_M = 0.5;
export const OUTPUT_USD_PER_M = 30;

// ─── Length caps (PRD §16.3) ──────────────────────────────────────────────────
export const OPTIONAL_GUIDANCE_MAX_BYTES = 4096;
export const WINNING_STRUCTURE_MAX_BYTES = 65536;
export const SOURCE_COPY_MAX_BYTES = 65536;
export const LIBRARY_VALUE_MAX_LEN = 200;
export const LIBRARY_DISPLAY_NAME_MAX_LEN = 200;
export const LANDING_PAGE_URL_MAX_LEN = 2048;
export const LIBRARY_METADATA_MAX_BYTES = 8192;
/**
 * Cap for the markdown `body` per parameter-library entry. 64 KB is large
 * enough for a multi-section persona (scenes, fears, dialogue notes) while
 * staying well below typical model context limits when several parameters
 * are injected into one prompt.
 */
export const LIBRARY_BODY_MAX_BYTES = 65536;
export const SOURCE_PACK_CONTENT_MAX_BYTES = 1048576;
export const STRUCTURED_PREMISE_PACKET_COUNT = 5;
export const STRUCTURED_PREMISE_TEXT_MAX_BYTES = 2000;
export const STRUCTURED_PREMISE_MARKDOWN_MAX_BYTES = 12000;

// ─── Prompt injection defence (PRD §16.3) ─────────────────────────────────────
// Rejects the source-pack file delimiter pattern in free-form user inputs.
// Pattern: "===== FILE:" with optional whitespace between ===== and FILE
export const PROMPT_INJECTION_PATTERN = /=====\s*FILE\s*:/;

// ─── Audit event taxonomy (PRD §12.6) ────────────────────────────────────────
export const AUDIT_EVENT_TYPES = [
  'auth.login.succeeded',
  'auth.login.failed',
  'auth.logout',
  'auth.bootstrap_admin_created',
  'user.created',
  'user.updated',
  'user.role_changed',
  'user.password_reset',
  'user.deactivated',
  'user.activated',
  'impersonation.started',
  'impersonation.ended',
  'product.created',
  'product.updated',
  'product.archived',
  'source_pack_file.created',
  'source_pack_file.updated',
  'source_pack_file.archived',
  'system_prompt.updated',
  'library.entry.created',
  'library.entry.updated',
  'library.entry.archived',
  'version.created',
  'version.reverted',
  'run.created',
  'run.completed',
  'run.failed',
  'run.status_updated',
  'session.revoked',
  'password.changed',
] as const;
export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];
